/**
 * CSRFMonitor - Monitor de detección CSRF
 * 
 * Intercepta fetch() y XMLHttpRequest para detectar requests POST/PUT/DELETE
 * que carecen de headers CSRF (X-CSRF-Token, X-XSRF-TOKEN, X-Requested-With).
 * Ignora requests del propio sensor al endpoint ADR. Reporta eventos CSRF
 * al transport cuando detecta protección faltante. Hooks defensivos que no
 * rompen el comportamiento original de fetch/XHR.
 */

import { ADR_CONSTANTS } from '../core/constants.js';
import SeverityCalculator from '../core/severity-calculator.js';
import { EVENT_CATALOG } from '../core/event-catalog.js';

const DEFAULT_CSRF_HEADERS = [
	'X-CSRF-Token',
	'X-XSRF-TOKEN',
	'X-Requested-With',
	'CSRF-Token',
	'Csrf-Token',
	'Anti-CSRF-Token',
	'__RequestVerificationToken'
];
const SAFE_HEADERS_FOR_SAMPLE = ['Content-Type', 'X-Requested-With', 'Accept', 'Origin'];

/**
 * Normaliza URL para clave de dedupe: origin + pathname (sin query/hash).
 * Mismo criterio que injection-monitor.js, para que dos requests al mismo
 * endpoint con distinto querystring deduplican como el mismo hallazgo.
 */
function normalizeUrlForDedupe(url, base) {
	if (!url || typeof url !== 'string') return '';
	try {
		const parsed = base ? new URL(url, base) : new URL(url);
		return (parsed.origin || '') + (parsed.pathname || '/');
	} catch (_) {
		return url;
	}
}

export class CSRFMonitor {
	constructor(config, severityCalculator = null) {
		this.config = config;
		this.transport = null;
		this.severityCalculator = severityCalculator || new SeverityCalculator(EVENT_CATALOG);
		this.originalFetch = null;
		this.originalXHRSend = null;
		this.originalXHROpen = null;
		this.originalXHRSetRequestHeader = null;
		this.hooksActive = false;
		this._dedupeMap = null;
		this._rateLimitTimestamps = null;
	}

	start(transport) {
		if (typeof window === 'undefined') {
			return;
		}

		if (this.hooksActive) {
			return;
		}

		this.transport = transport;
		this._dedupeMap = new Map();
		this._rateLimitTimestamps = [];
		this.wrapFetch();
		this.wrapXHR();
		this.hooksActive = true;
	}

	stop() {
		if (!this.hooksActive) {
			return;
		}

		this.restoreFetch();
		this.restoreXHR();
		this._dedupeMap = null;
		this._rateLimitTimestamps = null;
		this.hooksActive = false;
	}

	_getOption(key, defaultValue) {
		if (!this.config || typeof this.config.get !== 'function') return defaultValue;
		const v = this.config.get(key);
		return v !== undefined && v !== null ? v : defaultValue;
	}

	/**
	 * Dedupe: key = variant|method|normalizedUrl. TTL propio (csrfDedupeTtlMs)
	 * para no compartir presupuesto con injection-monitor.js.
	 */
	_isDedupe(variant, method, normalizedUrl) {
		const ttlMs = this._getOption('csrfDedupeTtlMs', 60000);
		const now = Date.now();
		const key = [variant, (method || '').toUpperCase(), normalizedUrl].join('|');

		for (const [k, expiresAt] of this._dedupeMap.entries()) {
			if (expiresAt <= now) this._dedupeMap.delete(k);
		}

		if (this._dedupeMap.has(key)) {
			return true;
		}
		this._dedupeMap.set(key, now + ttlMs);
		return false;
	}

	/**
	 * Rate limit: max N eventos por minuto (csrfMaxPerMinute).
	 */
	_isRateLimited() {
		const maxPerMin = this._getOption('csrfMaxPerMinute', 10);
		const now = Date.now();
		const oneMinAgo = now - 60000;
		this._rateLimitTimestamps = this._rateLimitTimestamps.filter(ts => ts > oneMinAgo);
		if (this._rateLimitTimestamps.length >= maxPerMin) {
			return true;
		}
		this._rateLimitTimestamps.push(now);
		return false;
	}

	wrapFetch() {
		if (typeof window.fetch === 'undefined') {
			return;
		}

		try {
			this.originalFetch = window.fetch;
			const self = this;

			window.fetch = function(url, options = {}) {
				try {
					if (self.isAdrEndpoint(url)) {
						return self.originalFetch.apply(this, arguments);
					}

					const method = (options.method || 'GET').toUpperCase();
					const headers = self.extractHeadersFromOptions(options);
					
					if (self.isMissingCSRFProtection(method, headers, url)) {
						self.reportDetection(method, url, headers);
					}
				} catch (e) {
				}

				try {
					return self.originalFetch.apply(this, arguments);
				} catch (e) {
					throw e;
				}
			};
		} catch (e) {
		}
	}

	wrapXHR() {
		if (typeof XMLHttpRequest === 'undefined') {
			return;
		}

		try {
			if (XMLHttpRequest.prototype.send) {
				this.originalXHRSend = XMLHttpRequest.prototype.send;
				this.originalXHROpen = XMLHttpRequest.prototype.open;
				this.originalXHRSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
				const self = this;

				XMLHttpRequest.prototype.open = function(method, url) {
					try {
						this._isAdrEndpoint = self.isAdrEndpoint(url);
						this._method = method;
						this._url = url;
					} catch (e) {
					}

					try {
						return self.originalXHROpen.apply(this, arguments);
					} catch (e) {
						throw e;
					}
				};

				XMLHttpRequest.prototype.setRequestHeader = function(name, value) {
					try {
						if (!this._requestHeaders) {
							this._requestHeaders = {};
						}
						this._requestHeaders[name.toLowerCase()] = value;
					} catch (e) {
					}

					try {
						return self.originalXHRSetRequestHeader.apply(this, arguments);
					} catch (e) {
						throw e;
					}
				};

				XMLHttpRequest.prototype.send = function(data) {
					try {
						if (this._isAdrEndpoint) {
							return self.originalXHRSend.apply(this, arguments);
						}

						const method = (this._method || 'GET').toUpperCase();
						const url = this._url || this.responseURL || '';
						const headers = self.extractHeadersFromXHR(this);
						
						if (self.isMissingCSRFProtection(method, headers, url)) {
							self.reportDetection(method, url, headers);
						}
					} catch (e) {
					}

					try {
						return self.originalXHRSend.apply(this, arguments);
					} catch (e) {
						throw e;
					}
				};
			}
		} catch (e) {
		}
	}

	extractHeadersFromOptions(options) {
		const headers = {};
		
		try {
			if (options.headers) {
				if (options.headers instanceof Headers) {
					for (const [key, value] of options.headers.entries()) {
						headers[key.toLowerCase()] = value;
					}
				} else if (typeof options.headers === 'object') {
					for (const [key, value] of Object.entries(options.headers)) {
						headers[key.toLowerCase()] = value;
					}
				}
			}
		} catch (e) {
		}

		return headers;
	}

	extractHeadersFromXHR(xhr) {
		const headers = {};
		
		try {
			if (xhr._requestHeaders) {
				for (const [key, value] of Object.entries(xhr._requestHeaders)) {
					headers[key.toLowerCase()] = value;
				}
			}
		} catch (e) {
		}

		return headers;
	}

	isMissingCSRFProtection(method, headers, url) {
		const stateChangingMethods = ['POST', 'PUT', 'DELETE'];
		
		if (!stateChangingMethods.includes(method)) {
			return false;
		}

		if (this.isAdrEndpoint(url)) {
			return false;
		}

		if (this.isAdrTokenEndpoint(url, headers)) {
			return false;
		}

		const csrfHeaders = this.getCSRFHeaderNames();
		const headerKeys = Object.keys(headers).map(k => k.toLowerCase());

		for (const csrfHeader of csrfHeaders) {
			if (headerKeys.includes(csrfHeader.toLowerCase())) {
				return false;
			}
		}

		return true;
	}

	getCSRFHeaderNames() {
		try {
			if (this.config && this.config.get) {
				const customHeaders = this.config.get('csrfHeaderNames');
				if (Array.isArray(customHeaders) && customHeaders.length > 0) {
					return customHeaders;
				}
			}
		} catch (e) {
		}

		return DEFAULT_CSRF_HEADERS;
	}

	isAdrEndpoint(url) {
		try {
			if (!url) return false;
			const urlString = typeof url === 'string' ? url : String(url);

			const endpoint = this.config && this.config.get ? this.config.get('endpoint') : ADR_CONSTANTS.DEFAULTS.endpoint;
			const scriptIntegrityEndpoint = this.config && this.config.get ? this.config.get('scriptIntegrityEndpoint') : null;

			const endpointsToIgnore = [endpoint];
			if (scriptIntegrityEndpoint && typeof scriptIntegrityEndpoint === 'string') {
				endpointsToIgnore.push(scriptIntegrityEndpoint);
			}

			for (const ep of endpointsToIgnore) {
				if (!ep || typeof ep !== 'string') continue;

				if (urlString === ep) return true;

				try {
					const absoluteEndpoint = new URL(ep, window.location.origin).toString();
					const absoluteUrl = new URL(urlString, window.location.origin).toString();
					if (absoluteUrl === absoluteEndpoint) return true;
				} catch (e) {
				}

				if (urlString.indexOf(ep) !== -1) return true;
			}

			return false;
		} catch (e) {
			return false;
		}
	}

	isAdrTokenEndpoint(url, headers) {
		try {
			if (!url) return false;
			const urlString = typeof url === 'string' ? url : String(url);

			const tokenEndpoint = this.config && this.config.get ? this.config.get('tokenEndpoint') : null;
			if (!tokenEndpoint || typeof tokenEndpoint !== 'string') {
				return false;
			}

			let matchesEndpoint = false;

			if (urlString === tokenEndpoint) {
				matchesEndpoint = true;
			} else {
				try {
					const absoluteEndpoint = new URL(tokenEndpoint, window.location.origin).toString();
					const absoluteUrl = new URL(urlString, window.location.origin).toString();
					if (absoluteUrl === absoluteEndpoint) {
						matchesEndpoint = true;
					}
				} catch (e) {
				}

				if (!matchesEndpoint && urlString.indexOf(tokenEndpoint) !== -1) {
					matchesEndpoint = true;
				}
			}

			if (!matchesEndpoint) {
				return false;
			}

			if (!headers || typeof headers !== 'object') {
				return false;
			}
			const headerKeys = Object.keys(headers).map(k => k.toLowerCase());
			return headerKeys.includes('x-sensor-integrity');
		} catch (e) {
			return false;
		}
	}

	getSafeHeadersSample(headers) {
		const sample = {};
		
		for (const safeHeader of SAFE_HEADERS_FOR_SAMPLE) {
			const lowerKey = safeHeader.toLowerCase();
			if (headers[lowerKey]) {
				sample[safeHeader] = headers[lowerKey];
			}
		}

		return sample;
	}

	reportDetection(method, url, headers) {
		if (!this.transport) {
			return;
		}

		const variant = this.determineVariant(method, url);

		if (this._dedupeMap) {
			const baseUrl = typeof window !== 'undefined' ? window.location.href : undefined;
			const normalizedUrl = normalizeUrlForDedupe(url, baseUrl);

			if (this._isDedupe(variant, method, normalizedUrl)) {
				return;
			}
			if (this._isRateLimited()) {
				return;
			}
		}

		const context = { method, url, headers };
		const result = this.severityCalculator.calculate('csrf', variant, context);

		const payload = {
			method: method,
			url: typeof url === 'string' ? url : String(url),
			missing_csrf_token: true,
			headers_sample: this.getSafeHeadersSample(headers)
		};

		this.transport.enqueue(
			ADR_CONSTANTS.EVENT_TYPES.CSRF,
			payload,
			result.severity,
			{
				source: 'http',
				origin_url: typeof window !== 'undefined' ? window.location.href : '',
				variant: result.variant,
				cvss_score: result.cvssScore,
				cvss_vector: result.cvssVector,
				owasp_category: result.owaspCategory,
				cwe_id: result.cweId,
				mitre_technique: result.mitreTechnique
			}
		);
	}

	determineVariant(method, url) {
		if (!url) {
			return 'post_without_token';
		}

		const urlLower = String(url).toLowerCase();

		if (urlLower.includes('payment') || urlLower.includes('transfer') ||
		    urlLower.includes('billing') || urlLower.includes('financial') ||
		    urlLower.includes('transaction') || urlLower.includes('purchase')) {
			return 'financial_operation';
		}

		if (urlLower.includes('admin') || urlLower.includes('delete') ||
		    urlLower.includes('grant') || urlLower.includes('permission') ||
		    urlLower.includes('user') || urlLower.includes('account') ||
		    urlLower.includes('config') || urlLower.includes('setting')) {
			return 'sensitive_endpoint';
		}

		if (urlLower.includes('password') || urlLower.includes('reset') || urlLower.includes('login') ||
		    urlLower.includes('auth') || urlLower.includes('signin') || urlLower.includes('signout')) {
			return 'authentication';
		}

		if (urlLower.includes('api') || urlLower.includes('graphql') || urlLower.includes('rest')) {
			return 'api_endpoint';
		}

		if (urlLower.includes('upload') || urlLower.includes('import') || urlLower.includes('file')) {
			return 'file_operation';
		}

		return 'post_without_token';
	}

	restoreFetch() {
		try {
			if (this.originalFetch && typeof window !== 'undefined') {
				window.fetch = this.originalFetch;
			}
		} catch (e) {
		}
	}

	restoreXHR() {
		try {
			if (this.originalXHRSend && typeof XMLHttpRequest !== 'undefined') {
				XMLHttpRequest.prototype.send = this.originalXHRSend;
			}
			if (this.originalXHROpen && typeof XMLHttpRequest !== 'undefined') {
				XMLHttpRequest.prototype.open = this.originalXHROpen;
			}
			if (this.originalXHRSetRequestHeader && typeof XMLHttpRequest !== 'undefined') {
				XMLHttpRequest.prototype.setRequestHeader = this.originalXHRSetRequestHeader;
			}
		} catch (e) {
		}
	}
}

export default CSRFMonitor;

